import React from 'react';
import { motion } from 'framer-motion';

// Nouveaux composants landing
import HeroSection from '../components/landing/HeroSection';
import ProblemSection from '../components/landing/ProblemSection';
import SolutionSection from '../components/landing/SolutionSection';
import SocialProofSection from '../components/landing/SocialProofSection';
import FinalCtaSection from '../components/landing/FinalCtaSection';

// Services et hooks existants
import { useTestPageData } from '../features/testPage/hooks/useTestPageData';

// Types
interface Prospect {
  id?: string;
  prenom?: string;
  nom?: string;
  site_url?: string;
  score?: number;
  quiz_fait?: boolean;
  quiz_commence?: boolean;
  token?: string;
  metadata?: {
    company?: string;
    domain_emelia?: string;
    industry?: string;
    location?: string;
    companyDescription?: string;
  };
}

// Utilitaires
const extractDomain = (url: string): string => {
  if (!url) return '';
  try {
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    }
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  } catch (error) {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  }
};

const getDomainFromProspect = (prospect: Prospect | null): string => {
  if (!prospect) return '';
  return prospect.site_url || prospect.metadata?.domain_emelia || '';
};

/**
 * Nouvelle landing page moderne avec design gris anthracite + orange
 * Structure optimisée pour la conversion B2B
 */
export const NewTestPage: React.FC = () => {
  const {
    // États
    error,
    prospect,
    
    // Actions
    startQuiz,
  } = useTestPageData();

  // Gestion des erreurs
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Erreur</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Données de l'entreprise
  const companyName = prospect?.metadata?.company || 
                     prospect?.nom || 
                     extractDomain(getDomainFromProspect(prospect)) || 
                     'votre entreprise';

  const handleCtaClick = () => {
    startQuiz();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header moderne minimaliste */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center space-x-4"
            >
              <img 
                src="/images/smart_impact_logo.jpg" 
                alt="Smart Impact" 
                className="h-10 object-contain" 
              />
              <div className="hidden sm:block text-white/80 text-sm font-medium">
                Agence Web Performance • Suisse
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* 1. Hero Section - Fond sombre */}
      <HeroSection 
        companyName={companyName}
        onCtaClick={handleCtaClick}
      />

      {/* 2. Section Problème - Fond clair */}
      <ProblemSection 
        companyName={companyName}
      />

      {/* 3. Section Solution - Fond sombre */}
      <SolutionSection 
        companyName={companyName}
      />

      {/* 4. Preuves sociales - Fond clair */}
      <SocialProofSection 
        companyName={companyName}
      />

      {/* 5. CTA final - Fond orange */}
      <FinalCtaSection 
        companyName={companyName}
        onCtaClick={handleCtaClick}
      />

      {/* Footer moderne */}
      <footer className="bg-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Footer Content */}
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              
              {/* Company Info */}
              <div>
                <div className="flex items-center mb-4">
                  <img 
                    src="/images/smart_impact_logo.jpg" 
                    alt="Smart Impact" 
                    className="h-8 object-contain mr-3" 
                  />
                  <span className="text-white font-semibold">Smart Impact</span>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Agence web spécialisée dans l'optimisation de performance et 
                  la génération de trafic qualifié pour les entreprises B2B.
                </p>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-white font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>Audit SEO technique</li>
                  <li>Optimisation performance</li>
                  <li>Stratégie de contenu</li>
                  <li>Génération de leads</li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-white font-semibold mb-4">Contact</h4>
                <div className="space-y-2 text-gray-400 text-sm">
                  <div>Suisse • France • Belgique</div>
                  <div>contact@smart-impact.ch</div>
                  <div>+41 XX XXX XX XX</div>
                </div>
              </div>
            </div>

            {/* Legal Notice */}
            <div className="border-t border-gray-800 pt-8">
              <div className="text-center text-gray-500 text-xs leading-relaxed">
                <p className="mb-2">
                  Cette analyse est basée uniquement sur des données publiques et des tendances sectorielles pour {companyName}. 
                  Elle ne contient aucune information personnelle ni confidentielle, et respecte pleinement les réglementations 
                  suisses et européennes sur la protection des données.
                </p>
                <p>
                  Son seul but : offrir aux dirigeants une lecture neutre et utile de leur présence numérique. 
                  Cette page est volontairement exclue des moteurs de recherche.
                </p>
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p>&copy; 2024 Smart Impact. Tous droits réservés.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Styles CSS personnalisés pour les animations */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeSlideUp {
          animation: fadeSlideUp 0.8s ease-out forwards;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #f97316;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #ea580c;
        }
      `}</style>
    </div>
  );
};

export default NewTestPage;
